import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  NgModule,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, NgxsModule, ofActionErrored, ofActionSuccessful, select } from '@ngxs/store';
import {
  AnimateModule,
  BasicPage,
  InputValidatorDirective,
  InvalidControlScrollDirective,
  ValidateMessageComponent,
  WwwRouteSlug,
} from '@pro-connect/shared';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { ToastrService } from 'ngx-toastr';
import { UserType } from '../../../../../../models';
import {
  AccountState,
  AccountStateAddEventPlace,
  AccountStateAddOrder,
  AccountStateRecalculateSummary,
  AccountStateRemoveEvent,
  AccountStateRemoveEventPlace,
} from '../../account.state';
import { IAccountOrder } from '../../models/account-order';
import { ThankYouModalComponent } from './components/thank-you-modal/thank-you-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Angulartics2GoogleTagManager } from 'angulartics2';

export type OrderFormType = {
  firstname: FormControl<string | null>;
  lastname: FormControl<string | null>;
  mobile: FormControl<string | null>;
  email: FormControl<string | null>;
  invoiceName: FormControl<string | null>;
  invoiceNip: FormControl<string | null>;
  invoiceStreet: FormControl<string | null>;
  invoiceZipCode: FormControl<string | null>;
  invoiceCity: FormControl<string | null>;
  events: FormControl<{ places: number; event: number }[] | null>;
  agreementPayment: FormControl<boolean | null>;
};

@NgModule({
  imports: [NgxsModule.forFeature([AccountState])],
})
class AccountStateLazyModule {}

@Component({
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    AnimateModule,
    ReactiveFormsModule,
    FormsModule,
    NgxsFormPluginModule,
    ValidateMessageComponent,
    InputValidatorDirective,
    InvalidControlScrollDirective,
    NgxMaskDirective,
    NgxMaskPipe,
    AccountStateLazyModule,
  ],
})
export class OrdersComponent extends BasicPage implements OnInit {
  private readonly _ngbModal: NgbModal = inject(NgbModal);
  private readonly _angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);

  public override readonly loading = select(AccountState.loading);
  public readonly events = select(AccountState.events);
  public readonly summPrice = select(AccountState.summPrice);
  public readonly WwwRouteSlug = WwwRouteSlug;
  public readonly UserType = UserType;
  public readonly formGroup: FormGroup<OrderFormType>;
  public readonly cartBtnBlock: WritableSignal<boolean> = signal(false);

  constructor(
    private readonly _actions$: Actions,
    private readonly _toastrService: ToastrService,
    private readonly _formBuilder: FormBuilder,
  ) {
    super();
    this.formGroup = this._formBuilder.group({
      firstname: new FormControl<string | null>({ value: '', disabled: true }, [Validators.required]),
      lastname: new FormControl<string | null>({ value: '', disabled: true }, [Validators.required]),
      mobile: new FormControl<string | null>({ value: '', disabled: true }, [Validators.required]),
      email: new FormControl<string | null>({ value: '', disabled: true }, [Validators.required]),
      invoiceName: new FormControl<string | null>({ value: '', disabled: true }, [Validators.required]),
      invoiceNip: new FormControl<string | null>({ value: '', disabled: true }, [Validators.required]),
      invoiceStreet: new FormControl<string | null>({ value: '', disabled: true }, [Validators.required]),
      invoiceZipCode: new FormControl<string | null>({ value: '', disabled: true }, [Validators.required]),
      invoiceCity: new FormControl<string | null>({ value: '', disabled: true }, [Validators.required]),
      events: new FormControl<{ places: number; event: number }[] | null>([], [Validators.required]),
      agreementPayment: new FormControl<boolean | null>(false, [Validators.requiredTrue]),
    });
    if (this.user()) {
      effect(() => {
        const me = this.user();
        setTimeout(() => {
          this.formGroup.patchValue({
            firstname: me?.firstname ? me.firstname : '-',
            lastname: me?.lastname ? me.lastname : '-',
            mobile: me?.mobile ? me.mobile : '-',
            email: me?.email ? me.email : '-',
            invoiceName: me?.name ? me.name : '-',
            invoiceCity: me?.city ? me?.city : '-',
            invoiceStreet: me?.street ? me?.street : '-',
            invoiceZipCode: me?.zip_code ? me?.zip_code : '-',
            invoiceNip: me?.nip ? me?.nip : '-',
            events: this.events().map((e: IAccountOrder) => ({
              event: e.id,
              places: Number(e.places),
            })),
          });
          this.formGroup.updateValueAndValidity();
        });
      });
    }
  }

  get f() {
    return this.formGroup.controls;
  }

  public ngOnInit(): void {
    this._store.dispatch([new AccountStateRecalculateSummary()]);
    this._setActionsSubscriptions();
    this._pushLayer();
  }

  public removePlace(event: IAccountOrder): void {
    this._store.dispatch([new AccountStateRemoveEventPlace({ event })]);
  }

  public addPlace(event: IAccountOrder): void {
    this._store.dispatch([new AccountStateAddEventPlace({ event })]);
  }

  public removeEvent(event: IAccountOrder): void {
    this._store.dispatch([new AccountStateRemoveEvent({ event })]);
  }

  public order(): void {
    if (!this.cartBtnBlock()) {
      this.formGroup.markAllAsTouched();
      if (this.formGroup.invalid) {
        this._toastrService.error('Proszę poprawnie uzupełnić formularz.', 'Błąd');
        return;
      }

      if (!this.f.events.value?.length) {
        this._toastrService.error('Formularz wypełniony niepoprawnie');
      } else {
        const eventsToAdd: {
          places: number;
          event: number;
        }[] = this.events().map((e: IAccountOrder) => {
          return { places: e.places, event: e.id };
        });

        this.cartBtnBlock.set(true);
        this.f.events.setValue([...eventsToAdd]);
        this._store.dispatch([new AccountStateAddOrder()]);
      }
    }
  }

  private _openOrderDetailsModal(): void {
    const ngbModalRef = this._ngbModal.open(ThankYouModalComponent, {
      centered: true,
      keyboard: false,
      backdrop: 'static',
      modalDialogClass: 'modal-container',
    });

    ngbModalRef.result.then(() => {
      this._store.dispatch([new Navigate(['/', this.WwwRouteSlug.ACCOUNT])]);
    });
  }

  private _setActionsSubscriptions(): void {
    this._actions$
      .pipe(takeUntilDestroyed(this._destroyRef), ofActionSuccessful(AccountStateAddOrder))
      .subscribe(() => {
        this._toastrService.success(`Zamówienie zostało zrealizowane`, 'Sukces');
        this._openOrderDetailsModal();
        this.formGroup.reset();
        this.cartBtnBlock.set(false);
      });
    this._actions$.pipe(takeUntilDestroyed(this._destroyRef), ofActionErrored(AccountStateAddOrder)).subscribe(() => {
      this._toastrService.error('Coś poszło nie tak.', 'Błąd');
      this.cartBtnBlock.set(false);
    });
  }

  private _pushLayer(): void {
    this._angulartics2GoogleTagManager.pushLayer({
      brand: 'LDB',
      language: 'pl',
      country: 'Pl',
      siteTypeLevel: this._isDevelopment ? 'test' : 'main',
      pageCategory: 'checkout',
      geoIPLocation: 'Pl',
      uiLoggedStatus: !!this.user() ? 'logged' : 'not logged',
      uiType: this.user()?.type,
    });
  }
}
